/**
 * QuickBytesTile - one concept tile in the bento grid.
 *
 * Renders a preview of the first highlight card. The tile is a single
 * button that opens the full-carousel modal - concept name, source
 * lesson, tags, and the bookmark action all live in the modal. Three
 * size variants drive the bento layout: `regular` (1x1), `tall` (1x2),
 * and `large` (2x2).
 */

import { CalendarCheck, Layers3 } from 'lucide-react';
import { Card } from '@ai-tutor/ui';
import { cn } from '@/lib/utils';
import type { HighlightsFeedCarousel } from '@/lib/api-client';
import { InlineMarkdown } from '@/components/core/markdown/markdown';
import { stripMath } from '@/lib/markdown/previewText';
import {
  TemplateCard,
  pickTemplateForConcept,
} from '@/components/features/highlights-template';
import { TILE_SPAN } from '@/components/features/mindspace/quick-bytes/QuickBytesGridStates';
import type { QuickBytesTileSize } from '@/lib/mindspace/bandPattern';
import { posterCard } from '@/components/features/highlights-template/poster';

// Re-exported so existing importers keep working; the definition lives with
// the pattern that produces it, in `lib/mindspace/bandPattern`.
export type { QuickBytesTileSize };

interface QuickBytesTileProps {
  carousel: HighlightsFeedCarousel;
  size: QuickBytesTileSize;
  onClick: () => void;
  /** The phone tile: poster, then a caption. See `PhoneTile`. */
  compact?: boolean;
}

export function QuickBytesTile({
  carousel,
  size,
  onClick,
  compact = false,
}: QuickBytesTileProps) {
  const firstCard = posterCard(carousel.cards);
  if (!firstCard) return null;
  if (compact) return <PhoneTile carousel={carousel} onClick={onClick} />;

  const count = carousel.cards.length;
  const hasMultiple = count > 1;

  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={`Open ${stripMath(carousel.concept_name)} highlights (${count} card${count === 1 ? '' : 's'})`}
      className={cn(
        TILE_SPAN[size],
        'group relative rounded-xl text-left transition-transform duration-200 hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 dark:focus-visible:ring-primary/40'
      )}
    >
      {/* Subtle stack: one offset layer peeking behind to whisper "more
          underneath". Only for multi-card concepts. */}
      {hasMultiple && (
        <span
          aria-hidden
          className='absolute inset-0 -z-10 translate-x-1.5 translate-y-1.5 rounded-xl border border-border/70 bg-card/50 dark:bg-secondary'
        />
      )}

      <Card
        className={cn(
          'relative flex h-full flex-col overflow-hidden p-0 backdrop-blur-none transition-shadow duration-200 group-hover:shadow-md'
        )}
      >
        <TemplateCard
          template={pickTemplateForConcept(carousel.concept_id)}
          card={firstCard}
          slideIndex={0}
          totalSlides={carousel.cards.length}
          conceptName={carousel.concept_name}
          variant='thumbnail'
        />

        {/* Due-today indicator floats above the templated surface so
            the template itself stays uncluttered. */}
        {carousel.is_due_today && (
          <div className='absolute left-3 top-3 z-10'>
            {size === 'large' ? (
              <span className='inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'>
                <CalendarCheck className='h-3 w-3' />
                Due today
              </span>
            ) : (
              <span
                className='block h-2 w-2 rounded-full bg-emerald-500'
                aria-label='Due today'
                title='Due today'
              />
            )}
          </div>
        )}

        {/* Card-count chip */}
        {hasMultiple && (
          <span className='absolute bottom-2 right-2 z-10 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white backdrop-blur-sm'>
            <Layers3 className='h-3 w-3' />
            {count}
          </span>
        )}
      </Card>
    </button>
  );
}

/**
 * The phone tile: the poster on top, and under it, on the card, the concept's
 * name and a mono line - "5 cards · due today".
 *
 * At 170px wide the poster is a thumbnail of a design, and it never said
 * which concept it was: the name lived only in the reader. The card count
 * and due dot it did carry were badges ON the design, over its type and its
 * arrow; the caption says both in words, and the offset "stack" layer behind
 * (clipped at the screen edge) goes, since "5 cards" now says it.
 */
function PhoneTile({
  carousel,
  onClick,
}: {
  carousel: HighlightsFeedCarousel;
  onClick: () => void;
}) {
  const count = carousel.cards.length;
  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={`Open ${stripMath(carousel.concept_name)} highlights (${count} card${count === 1 ? '' : 's'})`}
      className='group relative isolate flex flex-col overflow-hidden rounded-2xl border border-border bg-card text-left shadow-elevation-1 transition-transform duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.98] motion-reduce:active:scale-100'
    >
      <div aria-hidden='true' className='aspect-square w-full overflow-hidden'>
        <TemplateCard
          template={pickTemplateForConcept(carousel.concept_id)}
          card={posterCard(carousel.cards) ?? carousel.cards[0]}
          slideIndex={0}
          totalSlides={count}
          conceptName={carousel.concept_name}
          variant='thumbnail'
        />
      </div>
      <div className='border-t border-border px-3 pb-3 pt-2.5'>
        <p className='line-clamp-2 text-sm font-semibold leading-snug text-foreground'>
          <InlineMarkdown>{carousel.concept_name}</InlineMarkdown>
        </p>
        <p className='mt-1 font-mono text-xs uppercase text-muted-foreground'>
          {count} card{count === 1 ? '' : 's'}
          {carousel.is_due_today && (
            <span className='text-foreground'> · Due today</span>
          )}
        </p>
      </div>
    </button>
  );
}
